import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import TuneOutlined from '@mui/icons-material/TuneOutlined';

import type { LandingConfig } from './landingBuilderTypes';
import { LANDING_CANVAS_SCROLL_IDS } from './LandingCanvasLeftRail';
import {
  computeCanvasSectionMinHeightPx,
  rescaleBlocksYhPreservingPixelSize,
} from './canvasSectionHeightBlocks';
import { LANDING_CANVAS_FONT_OPTIONS, canvasFontIdFromStack } from './landingCanvasFonts';
import { ColorPickerPopover, normalizeHex } from './ColorPickerPopover';

export type BuilderBorderStyle =
  | 'none'
  | 'solid'
  | 'dashed'
  | 'dotted'
  | 'double'
  | 'groove'
  | 'ridge';

export type BuilderButtonStyle = {
  backgroundColor: string;
  color: string;
  borderRadius: number;
  fontSize: number;
  fontFamily?: string;
  borderWidth?: number;
  borderColor?: string;
  borderStyle?: BuilderBorderStyle;
  /** 0–1 — trong suốt màu nền nút (RGBA trong preview), không áp lên chữ */
  opacity?: number;
};

/** Ảnh / GIF / video — viền + bo góc + nền (letterbox) + trong suốt nền */
export type BuilderMediaStyle = {
  borderRadius: number;
  borderWidth?: number;
  borderColor?: string;
  borderStyle?: BuilderBorderStyle;
  backgroundColor?: string;
  /** 0–1 — trong suốt màu nền khung (không làm mờ ảnh/video) */
  opacity?: number;
  objectFit?: 'cover' | 'contain' | 'fill';
};

export type BuilderBlock =
  | {
      id: string;
      type: 'button';
      x: number;
      y: number;
      w: number;
      h: number;
      label: string;
      actionType: 'link' | 'scroll';
      href?: string;
      scrollTarget?: string;
      style: BuilderButtonStyle;
    }
  | {
      id: string;
      type: 'imageStrip';
      x: number;
      y: number;
      w: number;
      h: number;
      orientation: 'horizontal' | 'vertical';
      speedSec: number;
      images: string[];
    }
  | {
      id: string;
      type: 'imageSlider';
      x: number;
      y: number;
      w: number;
      h: number;
      /** URL ảnh theo thứ tự — hiển thị dải cuộn ngang */
      images: string[];
      /** trái→phải hoặc phải→trái — mặc định ltr */
      scrollDirection?: 'ltr' | 'rtl';
      /** Giây / vòng cuộn — mặc định 24 */
      scrollSpeedSec?: number;
      /** Cũ — chỉ dùng khi chưa có scrollSpeedSec */
      autoplaySec?: number;
      style: BuilderMediaStyle;
    }
  | {
      id: string;
      type: 'shapeText';
      x: number;
      y: number;
      w: number;
      h: number;
      text: string;
      shape: 'rectangle' | 'rounded';
      style: {
        fontSize: number;
        color: string;
        fontWeight?: number;
        textAlign?: 'left' | 'center' | 'right';
        backgroundColor?: string;
        padding?: number;
        /** 0–1 — trong suốt màu nền khung (RGBA trong preview), không áp lên chữ */
        opacity?: number;
        borderWidth?: number;
        borderColor?: string;
        borderStyle?: BuilderBorderStyle;
        /** Bo góc khung (px); nếu không có thì theo shape rounded/chữ nhật */
        frameRadius?: number;
        /** Google Fonts / stack CSS — xem landingCanvasFonts.ts */
        fontFamily?: string;
      };
    }
  | {
      id: string;
      type: 'eventList';
      x: number;
      y: number;
      w: number;
      h: number;
      batchSize: number;
    }
  | {
      id: string;
      type: 'media';
      x: number;
      y: number;
      w: number;
      h: number;
      /** ảnh + GIF dùng image; video dùng video */
      mediaKind: 'image' | 'video';
      src: string;
      poster?: string;
      alt?: string;
      style: BuilderMediaStyle;
    };

export type BuilderSection = {
  id: string;
  name: string;
  anchorId: string;
  height: number;
  backgroundColor?: string;
  blocks: BuilderBlock[];
};

function genId(prefix: string) {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

const PRESET_SCROLL_TARGETS: { value: string; label: string }[] = [
  { value: 'sec-hero', label: 'Hero (form cổ điểm)' },
  { value: 'landing-section-frames', label: 'Khung ảnh (form cổ điểm)' },
  { value: 'landing-section-pricing', label: 'Giá / Photobooth (form cổ điểm)' },
  { value: 'sec-events', label: 'Sự kiện (form cổ điểm)' },
  { value: 'lien-he', label: 'Footer / Liên hệ' },
];

function ensureBuilder(d: LandingConfig) {
  if (!d.builder || typeof d.builder !== 'object') {
    (d as LandingConfig).builder = { mode: 'canvas', sections: [] as BuilderSection[] };
  }
  const b = d.builder as Record<string, unknown>;
  if (!Array.isArray(b.sections)) b.sections = [];
  return b as { mode?: string; sections: BuilderSection[] };
}

function canvasBlockKindLabel(t: BuilderBlock['type']): string {
  switch (t) {
    case 'button':
      return 'Nút bấm';
    case 'imageStrip':
      return 'Dải ảnh (legacy)';
    case 'imageSlider':
      return 'Dải ảnh cuộn';
    case 'shapeText':
      return 'Khung chữ';
    case 'eventList':
      return 'Danh sách sự kiện';
    case 'media':
      return 'Ảnh / video';
    default:
      return 'Phần tử';
  }
}

const LP_DETAIL_SURFACE_SX = {
  p: 2,
  borderRadius: 2.5,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  boxShadow: '0 2px 14px rgba(15, 23, 42, 0.06)',
} as const;

type Props = {
  loading: boolean;
  config: LandingConfig;
  patch: (fn: (d: LandingConfig) => void) => void;
  selectedSectionId: string | null;
  selectedBlockId: string | null;
  onSelectedBlockIdChange: (id: string | null) => void;
};

const LandingPageCanvasEditor: React.FC<Props> = ({
  loading,
  config,
  patch,
  selectedSectionId,
  selectedBlockId,
  onSelectedBlockIdChange,
}) => {
  const builder = (config.builder ?? {}) as { sections?: BuilderSection[] };
  const sections = useMemo(() => (Array.isArray(builder.sections) ? builder.sections : []) as BuilderSection[], [builder.sections]);

  /** Ưu tiên trang đang chứa block — khớp «Chỉnh chi tiết trang» khi click phần tử */
  const selectedSection = useMemo(() => {
    if (selectedBlockId) {
      const sec = sections.find((s) => s.blocks.some((b) => b.id === selectedBlockId));
      if (sec) return sec;
    }
    return selectedSectionId ? sections.find((s) => s.id === selectedSectionId) ?? null : null;
  }, [sections, selectedSectionId, selectedBlockId]);

  const selectedBlock =
    selectedSection?.blocks.find((b) => b.id === selectedBlockId) ?? null;

  /** Luôn có thể xem chỉnh trang khi đang chỉnh phần tử — accordion «trang» không bị thu gọn mất */
  const [pageAccordionOpen, setPageAccordionOpen] = useState(true);
  const [blockAccordionOpen, setBlockAccordionOpen] = useState(true);

  useEffect(() => {
    if (selectedBlock) {
      setPageAccordionOpen(true);
      setBlockAccordionOpen(true);
    }
  }, [selectedBlock?.id]);

  useEffect(() => {
    setPageAccordionOpen(true);
  }, [selectedSection?.id]);

  const updateBlock = useCallback(
    (blockId: string, mut: (bl: BuilderBlock) => void) => {
      const sid = selectedSection?.id;
      if (!sid) return;
      patch((d) => {
        const b = ensureBuilder(d);
        const sec = (b.sections as BuilderSection[]).find((s) => s.id === sid);
        if (!sec) return;
        const blk = sec.blocks.find((x) => x.id === blockId);
        if (blk) mut(blk);
      });
    },
    [patch, selectedSection?.id],
  );

  const updateBlockLayout = useCallback(
    (blockId: string, field: 'x' | 'y' | 'w' | 'h', value: number) => {
      const sid = selectedSection?.id;
      if (!sid) return;
      const v = Math.max(0, Math.min(100, Number(value) || 0));
      patch((d) => {
        const b = ensureBuilder(d);
        const sec = (b.sections as BuilderSection[]).find((s) => s.id === sid);
        if (!sec) return;
        const blk = sec.blocks.find((x) => x.id === blockId);
        if (!blk) return;
        blk[field] = v;
      });
    },
    [patch, selectedSection?.id],
  );

  const isCanvasBuilder = (config.builder as { mode?: string } | undefined)?.mode === 'canvas';

  /** Canvas: chỉ các «trang» trong builder. Legacy: preset form cổ điển + trang canvas nếu có */
  const scrollAnchorOptions = useMemo(() => {
    const fromSections = sections.map((s, i) => ({
      value: s.anchorId || s.id,
      label: (s.name && String(s.name).trim()) || `Trang ${i + 1}`,
    }));
    if (isCanvasBuilder) {
      return fromSections;
    }
    const merged = [...PRESET_SCROLL_TARGETS];
    fromSections.forEach((o) => {
      if (!merged.some((m) => m.value === o.value)) merged.push(o);
    });
    return merged;
  }, [sections, isCanvasBuilder]);

  return (
    <Stack
      spacing={2}
      sx={{
        flex: '1 1 auto',
        minHeight: 0,
        minWidth: 0,
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'stretch',
      }}
    >
    <Stack spacing={2} sx={{ flex: '1 1 0%', minHeight: 0, overflow: 'auto', pr: 0.5 }}>
      <Box id={LANDING_CANVAS_SCROLL_IDS.page}>
      <Alert severity="info" sx={{ py: 0.5 }}>
        Quản lý trang trong <strong>Live preview</strong>. Đổi <strong>tên khu vực</strong> trên thanh preview; chỉnh <strong>chiều cao / màu nền</strong> và phần tử trong <strong>Chỉnh chi tiết</strong> bên dưới. Ảnh / video / GIF: icon <strong>Tải lên</strong> trên thanh bên preview. Lưu cấu hình để áp dụng.
      </Alert>

      {selectedSection ? (
              <Accordion
                expanded={pageAccordionOpen}
                onChange={(_, expanded) => setPageAccordionOpen(expanded)}
                disableGutters
                sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 1 } }}>
                  <Typography variant="subtitle2">Chỉnh chi tiết trang</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                    flexWrap="nowrap"
                    useFlexGap
                    sx={{ width: '100%', minWidth: 0 }}
                  >
                    <TextField
                      label="Chiều cao (px)"
                      size="small"
                      type="number"
                      value={selectedSection.height}
                      onChange={(e) => {
                        const n = parseInt(e.target.value, 10) || 400;
                        patch((d) => {
                          const b = ensureBuilder(d);
                          const sec = (b.sections as BuilderSection[]).find((s) => s.id === selectedSection.id);
                          if (!sec) return;
                          const oldH = Math.max(200, sec.height ?? 400);
                          const minH = computeCanvasSectionMinHeightPx(sec.blocks, oldH);
                          let newH = Math.min(20000, Math.max(200, n));
                          newH = Math.max(minH, newH);
                          if (oldH === newH) {
                            sec.height = newH;
                            return;
                          }
                          sec.height = newH;
                          rescaleBlocksYhPreservingPixelSize(sec.blocks, oldH, newH);
                        });
                      }}
                      sx={{ width: 118, flexShrink: 0 }}
                    />
                    <TextField
                      label="Màu nền trang"
                      size="small"
                      value={selectedSection.backgroundColor ?? ''}
                      onChange={(e) => {
                        patch((d) => {
                          const b = ensureBuilder(d);
                          const sec = (b.sections as BuilderSection[]).find((s) => s.id === selectedSection.id);
                          if (sec) sec.backgroundColor = e.target.value;
                        });
                      }}
                      placeholder="#0f172a hoặc để trống"
                      sx={{ flex: '1 1 0%', minWidth: 0, maxWidth: '100%' }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ mr: 0.5 }}>
                            <ColorPickerPopover
                              value={normalizeHex(selectedSection.backgroundColor)}
                              onChange={(hex) => {
                                patch((d) => {
                                  const b = ensureBuilder(d);
                                  const sec = (b.sections as BuilderSection[]).find((s) => s.id === selectedSection.id);
                                  if (sec) sec.backgroundColor = hex;
                                });
                              }}
                              label="Màu nền trang"
                              tooltip="Bảng màu & mã hex"
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Stack>
                </AccordionDetails>
              </Accordion>
      ) : (
        <Typography color="text.secondary">
          Chưa có trang — thêm trang trong Live preview (Từng trang) bằng nút trên mỗi khung.
        </Typography>
      )}
      </Box>

              {selectedSection && selectedBlock && (
                <Box id={LANDING_CANVAS_SCROLL_IDS.detail}>
                  <Accordion
                    expanded={blockAccordionOpen}
                    onChange={(_, expanded) => setBlockAccordionOpen(expanded)}
                    disableGutters
                    elevation={0}
                    sx={{
                      border: 'none',
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: (theme) =>
                        theme.palette.mode === 'dark'
                          ? '0 4px 28px rgba(0,0,0,0.5)'
                          : '0 4px 22px rgba(236, 72, 153, 0.14), 0 1px 4px rgba(15, 23, 42, 0.06)',
                      background: (theme) =>
                        theme.palette.mode === 'dark'
                          ? 'linear-gradient(165deg, rgba(236,72,153,0.12) 0%, rgba(15,23,42,0.94) 42%, #0b1220 100%)'
                          : 'linear-gradient(165deg, rgba(252, 231, 243, 0.95) 0%, #ffffff 36%, #f8fafc 100%)',
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={{
                        px: 2,
                        py: 1.25,
                        minHeight: 56,
                        bgcolor: (theme) =>
                          theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.55)',
                        backdropFilter: 'blur(8px)',
                        borderBottom: 1,
                        borderColor: 'divider',
                        '& .MuiAccordionSummary-content': { my: 0.5 },
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: 1, pr: 0.5 }}>
                        <Box
                          sx={{
                            width: 38,
                            height: 38,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
                            boxShadow: '0 2px 10px rgba(236,72,153,0.45)',
                            flexShrink: 0,
                          }}
                        >
                          <TuneOutlined sx={{ color: '#fff', fontSize: 22 }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" fontWeight={800} letterSpacing={-0.02} lineHeight={1.2}>
                            Chi tiết phần tử
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mt: 0.25 }}>
                            {canvasBlockKindLabel(selectedBlock.type)}
                          </Typography>
                        </Box>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2, pt: 2.25 }}>
                      <Stack spacing={2.25}>
                        <Paper elevation={0} sx={{ ...LP_DETAIL_SURFACE_SX, p: 1.75 }}>
                          <Typography
                            variant="overline"
                            color="text.secondary"
                            sx={{ letterSpacing: 1, fontWeight: 700, display: 'block', mb: 1 }}
                          >
                            Vị trí & kích thước
                          </Typography>
                          <Grid container spacing={1.25}>
                            <Grid item xs={6}>
                              <TextField
                                label="X %"
                                size="small"
                                fullWidth
                                type="number"
                                value={selectedBlock.x}
                                onChange={(e) => updateBlockLayout(selectedBlock.id, 'x', Number(e.target.value))}
                                InputProps={{ inputProps: { min: 0, max: 100, step: 0.5 } }}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Y %"
                                size="small"
                                fullWidth
                                type="number"
                                value={selectedBlock.y}
                                onChange={(e) => updateBlockLayout(selectedBlock.id, 'y', Number(e.target.value))}
                                InputProps={{ inputProps: { min: 0, max: 100, step: 0.5 } }}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Rộng %"
                                size="small"
                                fullWidth
                                type="number"
                                value={selectedBlock.w}
                                onChange={(e) => updateBlockLayout(selectedBlock.id, 'w', Number(e.target.value))}
                                InputProps={{ inputProps: { min: 0, max: 100, step: 0.5 } }}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Cao %"
                                size="small"
                                fullWidth
                                type="number"
                                value={selectedBlock.h}
                                onChange={(e) => updateBlockLayout(selectedBlock.id, 'h', Number(e.target.value))}
                                InputProps={{ inputProps: { min: 0, max: 100, step: 0.5 } }}
                              />
                            </Grid>
                          </Grid>
                        </Paper>
                  {selectedBlock.type === 'button' && (
                    <Paper elevation={0} sx={LP_DETAIL_SURFACE_SX}>
                    <Stack spacing={1.5}>
                      <TextField
                        label="Chữ nút"
                        size="small"
                        value={selectedBlock.label}
                        onChange={(e) => updateBlock(selectedBlock.id, (bl) => {
                          if (bl.type === 'button') bl.label = e.target.value;
                        })}
                      />
                      <FormControl size="small" fullWidth>
                        <InputLabel>Hành động</InputLabel>
                        <Select
                          label="Hành động"
                          value={selectedBlock.actionType}
                          onChange={(e) => updateBlock(selectedBlock.id, (bl) => {
                            if (bl.type === 'button') bl.actionType = e.target.value as 'link' | 'scroll';
                          })}
                        >
                          <MenuItem value="scroll">Cuộn tới khu vực</MenuItem>
                          <MenuItem value="link">Mở liên kết</MenuItem>
                        </Select>
                      </FormControl>
                      {selectedBlock.actionType === 'scroll' ? (
                        <FormControl size="small" fullWidth>
                          <InputLabel>Khu vực đích</InputLabel>
                          <Select
                            label="Khu vực đích"
                            value={selectedBlock.scrollTarget ?? ''}
                            onChange={(e) => updateBlock(selectedBlock.id, (bl) => {
                              if (bl.type === 'button') bl.scrollTarget = e.target.value;
                            })}
                          >
                            {scrollAnchorOptions.map((o) => (
                              <MenuItem key={o.value} value={o.value}>
                                {o.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <TextField
                          label="URL"
                          size="small"
                          value={selectedBlock.href ?? ''}
                          onChange={(e) => updateBlock(selectedBlock.id, (bl) => {
                            if (bl.type === 'button') bl.href = e.target.value;
                          })}
                        />
                      )}
                      <Stack direction="row" spacing={1}>
                        <TextField
                          label="Màu nền"
                          size="small"
                          value={selectedBlock.style.backgroundColor}
                          onChange={(e) => updateBlock(selectedBlock.id, (bl) => {
                            if (bl.type === 'button') bl.style.backgroundColor = e.target.value;
                          })}
                        />
                        <TextField
                          label="Màu chữ"
                          size="small"
                          value={selectedBlock.style.color}
                          onChange={(e) => updateBlock(selectedBlock.id, (bl) => {
                            if (bl.type === 'button') bl.style.color = e.target.value;
                          })}
                        />
                        <TextField
                          label="Bo góc (px)"
                          size="small"
                          type="number"
                          value={Math.min(100, selectedBlock.style.borderRadius)}
                          onChange={(e) =>
                            updateBlock(selectedBlock.id, (bl) => {
                              if (bl.type === 'button')
                                bl.style.borderRadius = Math.max(
                                  0,
                                  Math.min(100, Number(e.target.value) || 0),
                                );
                            })
                          }
                          InputProps={{ inputProps: { min: 0, max: 100 } }}
                          helperText="0–100"
                        />
                        <TextField
                          label="Cỡ chữ"
                          size="small"
                          type="number"
                          value={selectedBlock.style.fontSize}
                          onChange={(e) => updateBlock(selectedBlock.id, (bl) => {
                            if (bl.type === 'button') bl.style.fontSize = Number(e.target.value) || 14;
                          })}
                        />
                      </Stack>
                    </Stack>
                    </Paper>
                  )}
                  {selectedBlock.type === 'imageStrip' && (
                    <Paper elevation={0} sx={LP_DETAIL_SURFACE_SX}>
                    <Stack spacing={1.5}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Hướng</InputLabel>
                        <Select
                          label="Hướng"
                          value={selectedBlock.orientation}
                          onChange={(e) => updateBlock(selectedBlock.id, (bl) => {
                            if (bl.type === 'imageStrip') bl.orientation = e.target.value as 'horizontal' | 'vertical';
                          })}
                        >
                          <MenuItem value="horizontal">Ngang</MenuItem>
                          <MenuItem value="vertical">Dọc</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="Tốc độ (giây / vòng)"
                        size="small"
                        type="number"
                        value={selectedBlock.speedSec}
                        onChange={(e) => updateBlock(selectedBlock.id, (bl) => {
                          if (bl.type === 'imageStrip') bl.speedSec = Math.max(4, Number(e.target.value) || 18);
                        })}
                      />
                      <TextField
                        label="URL ảnh (mỗi dòng)"
                        size="small"
                        multiline
                        minRows={4}
                        value={(selectedBlock.images || []).join('\n')}
                        onChange={(e) => updateBlock(selectedBlock.id, (bl) => {
                          if (bl.type === 'imageStrip') {
                            bl.images = e.target.value.split('\n').map((s) => s.trim()).filter(Boolean);
                          }
                        })}
                      />
                    </Stack>
                    </Paper>
                  )}
                  {selectedBlock.type === 'imageSlider' && (
                    <Paper
                      elevation={0}
                      sx={{
                        ...LP_DETAIL_SURFACE_SX,
                        borderColor: 'primary.light',
                        background: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'linear-gradient(180deg, rgba(236,72,153,0.08) 0%, rgba(15,23,42,0.55) 100%)'
                            : 'linear-gradient(180deg, rgba(252,231,243,0.55) 0%, #ffffff 100%)',
                      }}
                    >
                      <Stack spacing={2}>
                        <Box
                          sx={{
                            p: 1.25,
                            borderRadius: 2,
                            bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(0,0,0,0.22)' : 'rgba(236,72,153,0.07)'),
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                            <strong>Ảnh trên preview:</strong> ô <strong>+</strong> hoặc kéo từ máy; <strong>×</strong> trên
                            thumbnail để xóa. Chuột phải khối để đổi chiều / tốc độ — hoặc chỉnh ngay bên dưới.
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1, fontWeight: 700 }}>
                            Chiều cuộn
                          </Typography>
                          <ToggleButtonGroup
                            exclusive
                            fullWidth
                            size="small"
                            value={selectedBlock.scrollDirection === 'rtl' ? 'rtl' : 'ltr'}
                            onChange={(_, v) => {
                              if (v == null) return;
                              updateBlock(selectedBlock.id, (bl) => {
                                if (bl.type === 'imageSlider') bl.scrollDirection = v;
                              });
                            }}
                            sx={{ mt: 1 }}
                          >
                            <ToggleButton value="ltr">Trái → phải</ToggleButton>
                            <ToggleButton value="rtl">Phải → trái</ToggleButton>
                          </ToggleButtonGroup>
                        </Box>

                        <Divider />

                        <Box>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1, fontWeight: 700 }}>
                              Tốc độ
                            </Typography>
                            <Chip
                              size="small"
                              color="primary"
                              label={`${selectedBlock.scrollSpeedSec ?? 24}s / vòng`}
                              sx={{ fontWeight: 700 }}
                            />
                          </Stack>
                          <Slider
                            value={selectedBlock.scrollSpeedSec ?? 24}
                            min={4}
                            max={120}
                            step={1}
                            onChange={(_, v) => {
                              const n = Array.isArray(v) ? v[0] : v;
                              updateBlock(selectedBlock.id, (bl) => {
                                if (bl.type === 'imageSlider') bl.scrollSpeedSec = n;
                              });
                            }}
                            valueLabelDisplay="auto"
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Số nhỏ hơn = cuộn nhanh hơn (tối thiểu 4 giây).
                          </Typography>
                          <TextField
                            label="Giây / vòng (nhập số)"
                            size="small"
                            type="number"
                            fullWidth
                            sx={{ mt: 1 }}
                            value={selectedBlock.scrollSpeedSec ?? 24}
                            onChange={(e) =>
                              updateBlock(selectedBlock.id, (bl) => {
                                if (bl.type === 'imageSlider') {
                                  const n = Number(e.target.value);
                                  bl.scrollSpeedSec = Number.isFinite(n) ? Math.max(4, Math.min(120, n)) : 24;
                                }
                              })
                            }
                            InputProps={{ inputProps: { min: 4, max: 120, step: 1 } }}
                          />
                        </Box>

                        <Divider />

                        <Stack spacing={2}>
                          <Box>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1, fontWeight: 700 }}>
                                Bo góc (px)
                              </Typography>
                              <Chip
                                size="small"
                                color="secondary"
                                label={`${selectedBlock.style.borderRadius ?? 12}px`}
                                sx={{ fontWeight: 700 }}
                              />
                            </Stack>
                            <Slider
                              value={selectedBlock.style.borderRadius ?? 12}
                              min={0}
                              max={64}
                              step={1}
                              onChange={(_, v) => {
                                const n = Array.isArray(v) ? v[0] : v;
                                updateBlock(selectedBlock.id, (bl) => {
                                  if (bl.type === 'imageSlider')
                                    bl.style.borderRadius = Math.max(0, Math.min(64, n));
                                });
                              }}
                              valueLabelDisplay="auto"
                            />
                            <TextField
                              label="Nhập số px"
                              size="small"
                              type="number"
                              fullWidth
                              sx={{ mt: 1 }}
                              value={selectedBlock.style.borderRadius ?? 12}
                              onChange={(e) =>
                                updateBlock(selectedBlock.id, (bl) => {
                                  if (bl.type === 'imageSlider')
                                    bl.style.borderRadius = Math.max(0, Math.min(64, Number(e.target.value) || 0));
                                })
                              }
                              InputProps={{ inputProps: { min: 0, max: 64, step: 1 } }}
                            />
                          </Box>
                          <TextField
                            label="Màu nền khung"
                            size="small"
                            fullWidth
                            value={selectedBlock.style.backgroundColor ?? ''}
                            onChange={(e) =>
                              updateBlock(selectedBlock.id, (bl) => {
                                if (bl.type === 'imageSlider') bl.style.backgroundColor = e.target.value;
                              })
                            }
                            placeholder="#0f172a"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start" sx={{ mr: 0.25 }}>
                                  <ColorPickerPopover
                                    value={normalizeHex(selectedBlock.style.backgroundColor)}
                                    onChange={(hex) => {
                                      updateBlock(selectedBlock.id, (bl) => {
                                        if (bl.type === 'imageSlider') bl.style.backgroundColor = hex;
                                      });
                                    }}
                                    label="Màu nền khung"
                                    tooltip="Chọn màu"
                                  />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Stack>
                      </Stack>
                    </Paper>
                  )}
                  {selectedBlock.type === 'shapeText' && (
                    <Paper elevation={0} sx={LP_DETAIL_SURFACE_SX}>
                    <Stack spacing={1.5}>
                      <TextField
                        label="Nội dung"
                        size="small"
                        multiline
                        minRows={3}
                        value={selectedBlock.text}
                        onChange={(e) => updateBlock(selectedBlock.id, (bl) => {
                          if (bl.type === 'shapeText') bl.text = e.target.value;
                        })}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Trong preview: nhấp đúp vào khung chữ để sửa; nhấp đúp lên một chữ để chọn cả từ (tách bởi dấu cách hoặc - , . …). Vùng xanh = chọn của trang; chọn màu trên toolbar hoặc chuột phải «Màu đoạn chọn».
                      </Typography>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Font chữ</InputLabel>
                        <Select
                          label="Font chữ"
                          value={canvasFontIdFromStack(selectedBlock.style.fontFamily)}
                          onChange={(e) => {
                            const id = e.target.value;
                            const opt = LANDING_CANVAS_FONT_OPTIONS.find((o) => o.id === id);
                            updateBlock(selectedBlock.id, (bl) => {
                              if (bl.type !== 'shapeText') return;
                              bl.style.fontFamily = opt?.stack ?? '';
                            });
                          }}
                          MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
                        >
                          {LANDING_CANVAS_FONT_OPTIONS.map((fo) => (
                            <MenuItem key={fo.id} value={fo.id} sx={fo.stack ? { fontFamily: fo.stack } : undefined}>
                              {fo.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Shape</InputLabel>
                        <Select
                          label="Shape"
                          value={selectedBlock.shape}
                          onChange={(e) => updateBlock(selectedBlock.id, (bl) => {
                            if (bl.type === 'shapeText') bl.shape = e.target.value as 'rectangle' | 'rounded';
                          })}
                        >
                          <MenuItem value="rectangle">Vuông</MenuItem>
                          <MenuItem value="rounded">Bo góc</MenuItem>
                        </Select>
                      </FormControl>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <TextField
                          label="Cỡ chữ"
                          size="small"
                          type="number"
                          value={selectedBlock.style.fontSize}
                          onChange={(e) => updateBlock(selectedBlock.id, (bl) => {
                            if (bl.type === 'shapeText') bl.style.fontSize = Number(e.target.value) || 14;
                          })}
                        />
                        <TextField
                          label="Màu chữ"
                          size="small"
                          value={selectedBlock.style.color}
                          onChange={(e) => updateBlock(selectedBlock.id, (bl) => {
                            if (bl.type === 'shapeText') bl.style.color = e.target.value;
                          })}
                        />
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>Căn</InputLabel>
                          <Select
                            label="Căn"
                            value={selectedBlock.style.textAlign ?? 'left'}
                            onChange={(e) => updateBlock(selectedBlock.id, (bl) => {
                              if (bl.type === 'shapeText') bl.style.textAlign = e.target.value as 'left' | 'center' | 'right';
                            })}
                          >
                            <MenuItem value="left">Trái</MenuItem>
                            <MenuItem value="center">Giữa</MenuItem>
                            <MenuItem value="right">Phải</MenuItem>
                          </Select>
                        </FormControl>
                      </Stack>
                    </Stack>
                    </Paper>
                  )}
                  {selectedBlock.type === 'media' && (
                    <Paper elevation={0} sx={LP_DETAIL_SURFACE_SX}>
                    <Stack spacing={1.5}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Loại</InputLabel>
                        <Select
                          label="Loại"
                          value={selectedBlock.mediaKind}
                          onChange={(e) =>
                            updateBlock(selectedBlock.id, (bl) => {
                              if (bl.type === 'media') bl.mediaKind = e.target.value as 'image' | 'video';
                            })
                          }
                        >
                          <MenuItem value="image">Hình ảnh / GIF</MenuItem>
                          <MenuItem value="video">Video</MenuItem>
                        </Select>
                      </FormControl>

                      {selectedBlock.src ? (
                        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                          Đang dùng: {selectedBlock.src}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="warning.main">
                          Chưa có file — chọn trong <strong>Thư viện</strong> phía trên hoặc dán (Ctrl+V).
                        </Typography>
                      )}

                      <FormControl size="small" fullWidth>
                        <InputLabel>Vừa khung</InputLabel>
                        <Select
                          label="Vừa khung"
                          value={selectedBlock.style.objectFit ?? 'contain'}
                          onChange={(e) =>
                            updateBlock(selectedBlock.id, (bl) => {
                              if (bl.type === 'media')
                                bl.style.objectFit = e.target.value as 'cover' | 'contain' | 'fill';
                            })
                          }
                        >
                          <MenuItem value="cover">Cover</MenuItem>
                          <MenuItem value="contain">Contain</MenuItem>
                          <MenuItem value="fill">Fill</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="Màu nền (letterbox khi contain)"
                        size="small"
                        fullWidth
                        value={selectedBlock.style.backgroundColor ?? ''}
                        onChange={(e) =>
                          updateBlock(selectedBlock.id, (bl) => {
                            if (bl.type === 'media') bl.style.backgroundColor = e.target.value;
                          })
                        }
                        placeholder="#0f172a"
                      />
                      <TextField
                        label="Alt (mô tả ảnh, tùy chọn)"
                        size="small"
                        fullWidth
                        value={selectedBlock.alt ?? ''}
                        onChange={(e) =>
                          updateBlock(selectedBlock.id, (bl) => {
                            if (bl.type === 'media') bl.alt = e.target.value;
                          })
                        }
                      />
                    </Stack>
                    </Paper>
                  )}
                  {selectedBlock.type === 'eventList' && (
                    <Paper elevation={0} sx={LP_DETAIL_SURFACE_SX}>
                    <Stack spacing={1}>
                    <TextField
                      label="Số thẻ mỗi lần tải"
                      size="small"
                      type="number"
                      value={selectedBlock.batchSize}
                      onChange={(e) => updateBlock(selectedBlock.id, (bl) => {
                        if (bl.type === 'eventList') bl.batchSize = Math.max(1, Number(e.target.value) || 6);
                      })}
                    />
                    </Stack>
                    </Paper>
                  )}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              )}
    </Stack>
    </Stack>
  );
};

export default LandingPageCanvasEditor;
